import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RegistrationStage, A2PRegistrationService } from '@galaxy/sms';
import { combineLatest, map, Observable, of } from 'rxjs';
import { InboxService } from '../inbox.service';
import { ConversationDetail } from '../interface/conversation.interface';
import { CONVERSATION_COUNTRY_TOKEN } from '../tokens';
import { AlertOptions, ConversationChannelService } from './conversation-channel.abstract';
import { InboxAlertService } from './inbox-alert.service';
import { getSubjectParticipantByType } from '../participant-utils';
import { ParticipantType } from '@vendasta/conversation';

@Injectable()
export class ConversationSMSService extends ConversationChannelService {
  private router = inject(Router);
  private readonly smsRegistrationService = inject(A2PRegistrationService);
  private readonly inboxService = inject(InboxService);
  private readonly country$ = inject(CONVERSATION_COUNTRY_TOKEN);
  private readonly canAccessSMS$ = this.inboxService.canAccessSMS$;
  private readonly inboxAlerter = inject(InboxAlertService);

  getChatComposerAlert(
    partnerId: string,
    accountGroupId: string,
    conversationDetail: ConversationDetail,
  ): Observable<AlertOptions | null> {
    const stage$ = this.smsRegistrationService.registrationStage$;
    const SMSNumber$ = this.inboxService.SMSNumber$;

    const checkContactInfo = this.inboxAlerter.getConversationCustomerErrors(conversationDetail);
    if (!checkContactInfo?.participant || checkContactInfo?.alert) return of(checkContactInfo?.alert ?? null);

    const accountGroupParticipant = getSubjectParticipantByType(
      conversationDetail?.participants,
      ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
    );

    return combineLatest([this.country$, this.canAccessSMS$, stage$, SMSNumber$]).pipe(
      map(([orgCountry, canAccessSMS, stage, smsNumber]) => {
        const country = orgCountry?.toLowerCase() || '';
        if (!this.inboxService.hasAccessByCountry(country)) return null;

        if (!canAccessSMS) {
          if (!accountGroupParticipant)
            return this.inboxAlerter.checkNotAvailableErrorForLeads(conversationDetail, country);
          return this.inboxAlerter.getSMSMessageEnabledError();
        }

        if (!smsNumber) return this.inboxAlerter.getSMSPhoneNumberError();

        if (country !== 'us') return null;

        return stage !== RegistrationStage.RegistrationComplete
          ? this.inboxAlerter.getSMSRegistrationError(accountGroupId)
          : null;
      }),
    );
  }
}
